/**
 * Browser Permissions Utility for KhelDrop
 * Strictly manages ONLY the 4 allowed permissions requested by the user:
 * 1. Live Location (GPS / Geolocation)
 * 2. Cookies (Authentication tokens & sport radar preferences)
 * 3. Notifications (Browser push & instant match alerts)
 * 4. Local Cache (CacheStorage & persistent offline asset storage)
 *
 * NOTE: Private Network Access (local device scanning), WebUSB, WebHID,
 * Bluetooth, camera, or invasive APIs are NEVER queried or triggered.
 */

export type NotificationPermissionState = NotificationPermission | 'unsupported';
export type LocationPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';
export type CookieConsentState = 'all' | 'essential' | 'prompt';
export type CachePermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

export interface KhelDropPermissionsState {
  location: LocationPermissionState;
  cookies: CookieConsentState;
  notifications: NotificationPermissionState;
  cache: CachePermissionState;
}

// -------------------------------------------------------------
// 1. NOTIFICATIONS
// -------------------------------------------------------------

export function isNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function getNotificationPermissionState(): NotificationPermissionState {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!isNotificationSupported()) {
    console.warn('Notifications are not supported in this browser environment.');
    return 'unsupported';
  }

  try {
    const permission = await Notification.requestPermission();
    if (typeof window !== 'undefined') {
      localStorage.setItem('kheldrop_notification_permission', permission);
    }

    if (permission === 'granted') {
      sendBrowserNotification('KhelDrop Live Alerts Enabled', {
        body: 'You will receive real-time notifications for game invites, claimed slots, and match updates.',
        icon: '/favicon.svg',
      });
    }

    return permission;
  } catch (err) {
    console.error('Error requesting notification permission:', err);
    return 'denied';
  }
}

export function sendBrowserNotification(title: string, options?: NotificationOptions): Notification | null {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return null;
  }

  try {
    return new Notification(title, {
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      ...options,
    });
  } catch (err) {
    console.warn('Failed to display browser notification:', err);
    return null;
  }
}

// -------------------------------------------------------------
// 2. LIVE LOCATION
// -------------------------------------------------------------

export function isLocationSupported(): boolean {
  return typeof window !== 'undefined' && 'geolocation' in navigator;
}

export function getLocationPermissionState(): LocationPermissionState {
  if (!isLocationSupported()) return 'unsupported';
  const saved = localStorage.getItem('kheldrop_location_permission');
  if (saved === 'granted') return 'granted';
  if (saved === 'denied') return 'denied';
  return 'prompt';
}

export async function requestLiveLocationPermission(
  onSuccess?: (lat: number, lng: number) => void,
  onError?: (err: GeolocationPositionError) => void
): Promise<LocationPermissionState> {
  if (!isLocationSupported()) return 'unsupported';

  return new Promise<LocationPermissionState>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (typeof window !== 'undefined') {
          localStorage.setItem('kheldrop_location_permission', 'granted');
          localStorage.setItem(
            'kheldrop_live_coords',
            JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude })
          );
        }
        if (onSuccess) {
          onSuccess(pos.coords.latitude, pos.coords.longitude);
        }
        resolve('granted');
      },
      (err) => {
        if (typeof window !== 'undefined') {
          localStorage.setItem('kheldrop_location_permission', 'denied');
        }
        if (onError) {
          onError(err);
        }
        resolve('denied');
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  });
}

// -------------------------------------------------------------
// 3. COOKIES & CONSENT
// -------------------------------------------------------------

export function getCookieConsentState(): CookieConsentState {
  if (typeof window === 'undefined') return 'prompt';
  const saved = localStorage.getItem('kheldrop_cookie_consent');
  if (saved === 'all') return 'all';
  if (saved === 'essential') return 'essential';
  return 'prompt';
}

export function requestCookieConsent(level: 'all' | 'essential' = 'all'): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('kheldrop_cookie_consent', level);
  localStorage.setItem(
    'kheldrop_cookie_settings',
    JSON.stringify({
      essential: true,
      performance: level === 'all',
      functional: level === 'all',
      matchmaking: level === 'all',
    })
  );
}

// -------------------------------------------------------------
// 4. CACHE & OFFLINE STORAGE
// -------------------------------------------------------------

export function isCacheSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    ('caches' in window || (typeof navigator !== 'undefined' && !!navigator.storage))
  );
}

export function getCachePermissionState(): CachePermissionState {
  if (!isCacheSupported()) return 'unsupported';
  const saved = localStorage.getItem('kheldrop_cache_permission');
  if (saved === 'granted') return 'granted';
  if (saved === 'denied') return 'denied';
  return 'prompt';
}

export async function requestCachePermission(): Promise<'granted' | 'unsupported'> {
  if (!isCacheSupported()) return 'unsupported';
  try {
    // 1. Initialize local cache bucket for instant asset load
    if ('caches' in window) {
      await caches.open('kheldrop-cache-v1');
    }
    // 2. Request persistent storage from browser if available
    if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.persist) {
      await navigator.storage.persist();
    }
    if (typeof window !== 'undefined') {
      localStorage.setItem('kheldrop_cache_permission', 'granted');
    }
    return 'granted';
  } catch (err) {
    console.warn('Cache activation note:', err);
    if (typeof window !== 'undefined') {
      localStorage.setItem('kheldrop_cache_permission', 'granted');
    }
    return 'granted';
  }
}

// -------------------------------------------------------------
// COORDINATED PERMISSION REQUEST (ONLY THE 4 PERMITTED ITEMS)
// -------------------------------------------------------------

export async function requestAllAllowedPermissions(
  onLocationSuccess?: (lat: number, lng: number) => void,
  onLocationError?: (err: GeolocationPositionError) => void
): Promise<KhelDropPermissionsState> {
  // 1. Enable Cookies
  requestCookieConsent('all');

  // 2. Enable Local Cache
  await requestCachePermission();

  // 3. Request Notifications
  const notifStatus = await requestNotificationPermission();

  // 4. Request Live Location
  const locStatus = await requestLiveLocationPermission(onLocationSuccess, onLocationError);

  if (typeof window !== 'undefined') {
    sessionStorage.setItem('kheldrop_permissions_dismissed', 'true');
    sessionStorage.setItem('kheldrop_location_dismissed', 'true');
  }

  return {
    location: locStatus,
    cookies: 'all',
    notifications: notifStatus,
    cache: 'granted',
  };
}

/**
 * Backward compatibility alias for earlier combined calls
 */
export async function requestCombinedPermissions(
  onLocationSuccess?: (lat: number, lng: number) => void,
  onLocationError?: (err: GeolocationPositionError) => void
) {
  const result = await requestAllAllowedPermissions(onLocationSuccess, onLocationError);
  return {
    notifications: result.notifications,
    location: result.location,
  };
}

export function getAppPermissionsSummary(): KhelDropPermissionsState {
  return {
    location: getLocationPermissionState(),
    cookies: getCookieConsentState(),
    notifications: getNotificationPermissionState(),
    cache: getCachePermissionState(),
  };
}
